import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    TemplateRef,
    ViewChild,
    inject,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { Plan } from '@bytel/bytel-sales';

import { DataCyDirective } from '@base/common/directives/data-cy.directive';
import { ModalService } from '@base/common/services/modal.service';
import { PriceComponent } from '@base/trilogy/price/price.component';

import { AppService } from '@services/app.services';
import { ConfigurationService } from '@services/configuration.service';
import { MessageService } from '@services/message.service';

import { SimContextModel } from '@models/sim-context.model';

import { ISim, SIM_TYPES } from '@interfaces/sim.interace';

import { ACTION_CLICK } from '@shared/tag/tag.interface';
import { TagService } from '@shared/tag/tag.service';

import { take } from 'rxjs';

import { EsimCompatibilityComponent } from './esim-compatibility-modal/esim-compatibility-modal.component';
import { EsimModalComponent } from './esim-modal/esim-modal.component';

@Component({
    selector: 'checkout-sim',
    standalone: true,
    imports: [CommonModule, RouterModule, DataCyDirective, PriceComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './sim.component.html',
    styleUrls: ['./sim.component.scss'],
})
export class SimComponent implements OnChanges {
    @ViewChild('customModalFooter') customModalFooter: TemplateRef<any>;

    @Input() simContext: SimContextModel;
    @Input() hasPhone: boolean;
    @Input() selectedSim: ISim;
    @Input() isRenew: boolean;
    @Input() isLoading: boolean;
    @Input() currentPlan?: Plan;
    @Output() simChoice = new EventEmitter<ISim>();

    public isIncompatible: boolean = false;
    public SIM_TYPES = SIM_TYPES;
    public choosenSim: ISim;
    public sims: ISim[];
    public shouldShowEsimLink: boolean;
    public shouldShowEsimCompatibilityLink: boolean = false;
    public isSummerEditionPlan: boolean = false;

    public readonly keepSim: ISim = {
        gencode: 'keepMySim',
        isCompatibleTerminal: true,
        isPrefered: true,
        price: 0,
        priceType: 0,
        type: SIM_TYPES.SIM,
    };

    protected messageService: MessageService = inject(MessageService);
    protected modalService: ModalService = inject(ModalService);
    protected appService: AppService = inject(AppService);
    protected tagService: TagService = inject(TagService);
    private configService: ConfigurationService = inject(ConfigurationService);

    ngOnChanges(): void {
        this.refresh();
    }

    public onChoice(event: PointerEvent, sim: ISim | undefined): void {
        this.choosenSim = sim;
        if (sim) {
            if (this.isRenew) {
                this.sendTag(
                    sim.type === 'sim' ? 'Nouvelle_Carte_SIM' : 'Nouvelle_eSIM',
                    ACTION_CLICK.SELECTION,
                    'Selection_SIM',
                );
            } else {
                this.sendTag(sim.type === 'sim' ? 'Carte_SIM' : 'eSIM', ACTION_CLICK.SELECTION, 'Selection_SIM');
            }
        }

        if (sim && this.hasPhone && !sim.isCompatibleTerminal) {
            event.preventDefault();
            this.showCompatibilityErrorModal();
        } else {
            this.simChoice.emit(sim);
        }
    }

    public confirmChoice(keepChoice: boolean): void {
        if (!keepChoice) {
            this.simChoice.emit(this.choosenSim);
        }
    }

    public showCompatibilityModal(): void {
        this.sendTag('Voir_telephones_compatibles', ACTION_CLICK.CLICK, 'Bloc_SIM');
        this.messageService.openModal(EsimCompatibilityComponent);
    }

    public showCompatibilityErrorModal(): void {
        this.messageService.openModal(EsimModalComponent, {
            title: `${this.choosenSim.type === SIM_TYPES.QRESIM ? "L'eSIM" : 'La carte SIM'} n'est pas compatible avec cet appareil`,
        });

        this.modalService.afterClosed.pipe(take(1)).subscribe((result) => {
            if (result?.data?.simChoice !== undefined) {
                this.confirmChoice(result.data.simChoice);
            }
        });
    }

    private refresh(): void {
        if (this.simContext) {
            this.sims = this.simContext.sims;
            this.shouldShowEsimLink = this.selectedSim.type === SIM_TYPES.QRESIM && this.refreshLinkState();
            this.shouldShowEsimCompatibilityLink = this.selectedSim.type === SIM_TYPES.QRESIM &&this.refreshEsimCompatibilityLink();
        }
        this.isSummerEditionPlan =
            this.currentPlan.gencode === this.configService.getData()?.global?.specialOffers?.summerEdition;
    }

    private refreshLinkState(): boolean {
        const esim = this.simContext.sims.find((sim) => sim.type === SIM_TYPES.QRESIM);
        if (esim && !this.hasPhone) {
            return true;
        }
        return false;
    }

    private refreshEsimCompatibilityLink(): boolean { 
        const esim = this.simContext.sims.find((sim) => sim.type === SIM_TYPES.QRESIM);
        if (!esim || !this.hasPhone || (this.hasPhone && esim?.isCompatibleTerminal)) {
            return false;
        }

        return true;
    }

    private sendTag(event: string, action: ACTION_CLICK, categorie: string): void {
        this.tagService.send({
            nomClic: event,
            categorieClic: categorie,
            actionClic: action,
        });
    }
}
